import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

import { JOB_TYPE_OPTIONS } from '../constants/jobTypes';
const CATEGORY_OPTIONS = [
  { value: 'legal', label: '法律合规' },
  { value: 'finance', label: '财务审计' },
  { value: 'business', label: '商务拓展' },
  { value: 'engineering', label: '工程技术' },
  { value: 'hr', label: '人力资源' },
  { value: 'supply-chain', label: '供应链' },
  { value: 'other', label: '其他' },
];

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
      padding: '14px 14px', minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--secondary)', margin: '4px 0' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{sub}</div>}
    </div>
  );
}

function jobTypeLabel(type) {
  const map = {
    'overseas-market': '国内负责海外市场',
    'global-market': '国内负责全球市场',
    'foreign-domestic': '国内外资企业',
    'expat': '外派',
    'local-hire': '海外本地招聘',
  };
  return map[type] || type || '未分类';
}

function AdminImport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [tab, setTab] = useState('stats');
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  // 用户管理
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.error || '获取数据失败');
    }
    setStatsLoading(false);
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchJobs = async () => {
    try {
      const res = await api.get('/jobs/admin/all?limit=200');
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCandidates = async (searchVal) => {
    try {
      const params = searchVal ? `?search=${encodeURIComponent(searchVal)}` : '';
      const res = await api.get(`/candidates/admin/all${params}`);
      setCandidates(res.data.candidates || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await api.get('/comments/admin/all?limit=100');
      setComments(res.data.comments || []);
    } catch (err) {
      console.error(err);
    }
    setCommentsLoading(false);
  };

  const fetchUsers = async (searchVal, includeDeleted) => {
    setUsersLoading(true);
    try {
      const qs = new URLSearchParams();
      if (searchVal) qs.set('search', searchVal);
      if (includeDeleted) qs.set('includeDeleted', 'true');
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const res = await api.get(`/admin/users${suffix}`);
      setAllUsers(res.data.users || []);
    } catch (err) {
      console.error(err);
    }
    setUsersLoading(false);
  };

  const handleToggleRole = async (uid, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`确定将此用户角色改为 ${newRole === 'admin' ? '管理员' : '普通用户'}？`)) return;
    try {
      await api.put(`/admin/users/${uid}/role`, { role: newRole });
      fetchUsers(userSearch, showDeletedUsers);
    } catch (err) {
      alert(err.response?.data?.error || '操作失败');
    }
  };

  const handleToggleDisable = async (uid, isDisabled) => {
    if (!window.confirm(isDisabled ? '确定启用此用户？' : '确定禁用此用户？禁用后该用户将无法登录。')) return;
    try {
      await api.put(`/admin/users/${uid}/disable`, { disabled: !isDisabled });
      fetchUsers(userSearch, showDeletedUsers);
    } catch (err) {
      alert(err.response?.data?.error || '操作失败');
    }
  };

  const handleDeleteUser = async (uid, username) => {
    const msg = `⚠️ 确定注销用户 "${username}" 吗？\n\n` +
      `执行后：\n` +
      `• 用户名/邮箱/昵称/头像/性别等个人信息会被抹去\n` +
      `• 该用户立即无法登录\n` +
      `• 他发布的岗位会保留，作者显示为"已注销用户"\n` +
      `• 岗位的联系方式会被替换为"请联系管理员转介"\n` +
      `• 该用户的人才/专家档案会匿名化\n` +
      `• 评论和站内消息保留原文，作者显示为"已注销用户"\n\n` +
      `操作不可逆。`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.delete(`/admin/users/${uid}`);
      alert(res.data.message + (res.data.jobsAnonymized ? `（${res.data.jobsAnonymized} 个岗位已处理）` : ''));
      fetchUsers(userSearch, showDeletedUsers);
    } catch (err) {
      alert(err.response?.data?.error || '注销失败');
    }
  };

  useEffect(() => {
    if (tab === 'manage') fetchJobs();
    if (tab === 'candidates') fetchCandidates();
    if (tab === 'comments') fetchComments();
    if (tab === 'stats') fetchStats();
    if (tab === 'users') fetchUsers();
  }, [tab]);

  const handleParse = async () => {
    if (!rawText.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/jobs/admin/quick-import', { rawText });
      setParsed(res.data.parsed);
      setMessage('');
    } catch (err) {
      setMessage(err.response?.data?.error || '解析失败');
    }
    setLoading(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const validExts = ['.pdf', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    const name = file.name || '';
    const ext = name.includes('.') ? name.substring(name.lastIndexOf('.')).toLowerCase() : '';
    const isImage = (file.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
    if (!validExts.includes(ext) && !isImage) {
      setMessage('不支持的文件格式，请上传 PDF、Word (.docx)、文本或图片文件');
      return;
    }
    setLoading(true);
    setFileName(file.name);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      // 不要手动设置 Content-Type，浏览器会自动带上 boundary。
      const res = await api.post('/jobs/smart-parse-file', formData, {
        timeout: 180000,
      });
      setParsed(res.data.parsed);
      if (res.data.rawText) setRawText(res.data.rawText);
      setMessage('');
    } catch (err) {
      setMessage(err.response?.data?.error || '文件解析失败');
    }
    setLoading(false);
    setFileName('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };

  // 粘贴：支持直接粘贴截图（OCR）或 Finder 复制的文件
  const handlePaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    if (cd.files && cd.files.length > 0) {
      e.preventDefault();
      handleFileUpload(cd.files[0]);
      return;
    }
    const items = cd.items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file' && it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          const named = new File(
            [file],
            file.name && file.name !== 'image.png' ? file.name : `pasted-${Date.now()}.png`,
            { type: file.type || 'image/png' }
          );
          handleFileUpload(named);
          return;
        }
      }
    }
    // 纯文本粘贴：保持 textarea 默认行为
  };

  const handleFieldChange = (field, value) => {
    setParsed(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!parsed) return;
    setLoading(true);
    try {
      const res = await api.post('/jobs/admin/confirm-import', { jobs: [parsed] });
      setMessage(`✅ ${res.data.message}`);
      setParsed(null);
      setRawText('');
    } catch (err) {
      setMessage(err.response?.data?.error || '导入失败');
    }
    setLoading(false);
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('确定删除这个岗位？')) return;
    try {
      await api.delete(`/jobs/${jobId}`);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setMessage('✅ 已删除');
    } catch (err) {
      setMessage(err.response?.data?.error || '删除失败');
    }
  };

  const openEditJob = (job) => {
    // 把日期字段截成 YYYY-MM-DD 以便 input[type=date] 识别
    const pubDate = job.publishDate ? String(job.publishDate).slice(0, 10) : '';
    setEditingJob({
      id: job.id,
      title: job.title || '',
      company: job.company || '',
      location: job.location || '',
      jobType: job.jobType || 'overseas-market',
      category: job.category || 'other',
      salary: job.salary || '',
      contactInfo: job.contactInfo || '',
      publishDate: pubDate,
      description: job.description || '',
      requirements: job.requirements || '',
    });
  };

  const handleEditField = (field, value) => {
    setEditingJob(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingJob) return;
    setEditSaving(true);
    try {
      const { id, ...payload } = editingJob;
      await api.put(`/jobs/${id}`, payload);
      // 更新列表中对应条目
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...payload } : j));
      setMessage('✅ 岗位已更新');
      setEditingJob(null);
    } catch (err) {
      setMessage(err.response?.data?.error || '更新失败');
    }
    setEditSaving(false);
  };

  const handleToggleHide = async (job) => {
    try {
      const res = await api.put(`/jobs/admin/${job.id}/hide`);
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, isHidden: res.data.isHidden } : j));
      setMessage(`✅ ${res.data.message}`);
    } catch (err) {
      setMessage(err.response?.data?.error || '操作失败');
    }
  };

  const handleToggleExpire = async (job) => {
    try {
      const res = await api.put(`/jobs/admin/${job.id}/expire`);
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, isExpired: res.data.isExpired } : j));
      setMessage(`✅ ${res.data.message}`);
    } catch (err) {
      setMessage(err.response?.data?.error || '操作失败');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('⚠️ 确定清除所有岗位？此操作不可恢复！')) return;
    if (!window.confirm('再次确认：清除全部岗位数据？')) return;
    try {
      await api.delete('/jobs/admin/clear-all');
      setJobs([]);
      setMessage('✅ 所有岗位已清除');
    } catch (err) {
      setMessage(err.response?.data?.error || '清除失败');
    }
  };

  const handleTogglePublic = async (candidate) => {
    try {
      await api.put(`/candidates/admin/${candidate.id}`, { isPublic: !candidate.isPublic });
      setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, isPublic: c.isPublic ? 0 : 1 } : c));
      setMessage('✅ 已更新');
    } catch (err) {
      setMessage(err.response?.data?.error || '更新失败');
    }
  };

  const handleDeleteCandidate = async (id) => {
    if (!window.confirm('确定删除该候选人资料？')) return;
    try {
      await api.delete(`/candidates/admin/${id}`);
      setCandidates(prev => prev.filter(c => c.id !== id));
      setMessage('✅ 已删除');
    } catch (err) {
      setMessage(err.response?.data?.error || '删除失败');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('确定删除这条评论？')) return;
    try {
      await api.delete(`/comments/admin/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setMessage('✅ 评论已删除');
    } catch (err) {
      setMessage(err.response?.data?.error || '删除失败');
    }
  };

  const handleCandidateSearch = (e) => {
    e.preventDefault();
    fetchCandidates(candidateSearch);
  };

  if (!user || user.role !== 'admin') return null;

  // 用户管理 tab 表格列多，需要更宽；其他 tab 用 800 即可
  const containerMaxWidth = tab === 'users' ? 1280 : 800;

  return (
    <div className="container" style={{ maxWidth: containerMaxWidth, padding: '20px 16px' }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: 16 }}>管理后台</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--border)', overflowX: 'auto', flexWrap: 'nowrap' }}>
        {[
          ['stats', '📊 数据概览'],
          ['users', '🔑 用户管理'],
          ['manage', '📂 岗位管理'],
          ['comments', '💬 评论管理'],
          ['candidates', '👥 人才管理'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: tab === key ? 700 : 400, fontSize: '1rem',
              borderBottom: tab === key ? '2px solid var(--primary)' : '2px solid transparent',
              color: tab === key ? 'var(--primary)' : 'var(--text-light)',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {message && (
        <div style={{ padding: '10px 16px', background: message.includes('✅') ? '#e8f5e9' : '#ffebee',
          borderRadius: 8, marginBottom: 16, fontSize: '0.9rem' }}>
          {message}
        </div>
      )}

      {/* Stats (数据概览) Tab */}
      {tab === 'stats' && (
        <div>
          {statsLoading && <div style={{ color: 'var(--text-light)' }}>加载中...</div>}
          {!statsLoading && !stats && (
            <div style={{ color: 'var(--text-light)' }}>
              暂无数据。<button onClick={fetchStats} style={{ marginLeft: 8 }} className="btn btn-secondary btn-sm">重新加载</button>
            </div>
          )}
          {stats && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
                  更新时间：{new Date(stats.generatedAt).toLocaleString('zh-CN')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => navigate('/admin/pending')}
                    style={{ padding: '6px 12px', fontSize: '0.85rem', borderRadius: 4, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                    📥 抓取审核队列
                  </button>
                  <button onClick={fetchStats} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                    🔄 刷新
                  </button>
                </div>
              </div>

              {/* 核心指标卡片 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="注册用户总数" value={stats.users.total} sub={`今日 +${stats.users.today} · 7日 +${stats.users.week}`} />
                <StatCard label="活跃岗位" value={stats.jobs.active} sub={`总发布 ${stats.jobs.total} · 今日 +${stats.jobs.today}`} />
                <StatCard label="岗位浏览总次数" value={stats.jobs.totalViews} sub="累计" />
                <StatCard label="公开人才档案" value={stats.candidates.public} sub={`总档案 ${stats.candidates.total}`} />
                <StatCard label="评论总数" value={stats.comments.total} sub={`今日 +${stats.comments.today}`} />
                <StatCard label="站内消息" value={stats.messages.total} sub="累计" />
                <StatCard label="反馈" value={stats.feedback.total} sub={`待处理 ${stats.feedback.pending}`} />
                <StatCard label="隐藏 / 已过期岗位" value={`${stats.jobs.hidden} / ${stats.jobs.expired}`} sub="管理员已处理" />
              </div>

              {/* 岗位类型分布 */}
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>活跃岗位类型分布</h3>
                {stats.jobs.byType.length === 0 && <div style={{ color: 'var(--text-light)' }}>暂无</div>}
                {stats.jobs.byType.map(row => (
                  <div key={row.type} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.9rem' }}>
                    <span>{jobTypeLabel(row.type)}</span>
                    <span style={{ fontWeight: 600 }}>{row.count}</span>
                  </div>
                ))}
              </div>

              {/* 浏览量 Top 10 */}
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>浏览量 Top 10 岗位</h3>
                {stats.jobs.topViewed.length === 0 && <div style={{ color: 'var(--text-light)' }}>暂无</div>}
                {stats.jobs.topViewed.map((j, i) => (
                  <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.9rem' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      #{i + 1} {j.title} · <span style={{ color: 'var(--text-light)' }}>{j.company}</span>
                    </span>
                    <span style={{ fontWeight: 600, marginLeft: 12 }}>👁 {j.viewCount || 0}</span>
                  </div>
                ))}
              </div>

              {/* 最近注册用户 */}
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>最近注册用户</h3>
                {stats.recentUsers.length === 0 && <div style={{ color: 'var(--text-light)' }}>暂无</div>}
                {stats.recentUsers.map(u => (
                  <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 500 }}>{u.displayName || u.username}</span>
                      <span style={{ color: 'var(--text-light)', marginLeft: 8 }}>{u.email}</span>
                      {u.role === 'admin' && <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#c00' }}>admin</span>}
                    </div>
                    <span style={{ color: 'var(--text-light)', fontSize: '0.8rem', marginLeft: 8 }}>
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString('zh-CN') : ''}
                    </span>
                  </div>
                ))}
              </div>

              {/* 最近发布岗位 */}
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>最近发布岗位</h3>
                {stats.recentJobs.length === 0 && <div style={{ color: 'var(--text-light)' }}>暂无</div>}
                {stats.recentJobs.map(j => (
                  <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 500 }}>{j.title}</span>
                      <span style={{ color: 'var(--text-light)', marginLeft: 8 }}>{j.company} · {j.location}</span>
                    </div>
                    <span style={{ color: 'var(--text-light)', fontSize: '0.8rem', marginLeft: 8 }}>
                      {j.createdAt ? new Date(j.createdAt).toLocaleDateString('zh-CN') : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'comments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
              共 {comments.length} 条评论
            </span>
            <button
              className="btn btn-primary"
              onClick={fetchComments}
              style={{ fontSize: '0.85rem' }}
            >
              刷新
            </button>
          </div>

          {commentsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
              加载中...
            </div>
          ) : comments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
              暂无评论数据
            </div>
          ) : (
            comments.map(c => (
              <div key={c.id} style={{
                padding: '14px 0', borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                        {c.isAnonymous ? '匿名用户' : (c.authorNickname || c.authorName || c.authorUsername || '-')}
                      </span>
                      {c.isAnonymous && (
                        <span style={{ fontSize: '0.72rem', padding: '1px 6px', background: '#f3f4f6', borderRadius: 4, color: '#6b7280' }}>
                          匿名
                        </span>
                      )}
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                        {c.authorEmail || ''}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.88rem', marginBottom: 4, wordBreak: 'break-all' }}>
                      {c.content}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                      岗位：{c.jobTitle || '-'} · {c.jobCompany || '-'}
                      {' · '}
                      {new Date(c.createdAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteComment(c.id)}
                    style={{
                      background: 'none', border: 'none', color: '#ef5350',
                      cursor: 'pointer', fontSize: '0.85rem', padding: '4px 8px',
                      flexShrink: 0,
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Candidates Tab */}
      {tab === 'candidates' && (
        <div>
          <form onSubmit={handleCandidateSearch} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              className="form-input"
              placeholder="搜索姓名、职位、技能、邮箱..."
              value={candidateSearch}
              onChange={e => setCandidateSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <button type="submit" className="btn btn-primary" style={{ fontSize: '0.85rem' }}>搜索</button>
          </form>

          <div style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: 12 }}>
            共 {candidates.length} 位候选人
          </div>

          {candidates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
              暂无候选人数据
            </div>
          ) : (
            candidates.map(c => (
              <div key={c.id} style={{
                padding: '14px 0', borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {c.name}
                      <span style={{
                        display: 'inline-block', marginLeft: 8, fontSize: '0.75rem', padding: '2px 8px',
                        borderRadius: 4, background: c.isPublic ? '#e8f5e9' : '#fff3e0',
                        color: c.isPublic ? '#2e7d32' : '#e65100',
                      }}>
                        {c.isPublic ? '公开' : '私密'}
                      </span>
                    </div>
                    {c.title && <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: 2 }}>{c.title}</div>}
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: 4 }}>
                      {c.location && <span>📍 {c.location}  </span>}
                      {c.experience && <span>💼 {c.experience}  </span>}
                      {c.languages && <span>🗣️ {c.languages}</span>}
                    </div>
                    {c.skills && <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: 2 }}>🛠️ {c.skills}</div>}
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginTop: 4 }}>
                      {c.contactEmail && <span>📧 {c.contactEmail}  </span>}
                      {c.contactPhone && <span>📱 {c.contactPhone}  </span>}
                      {c.linkedIn && <span>🔗 LinkedIn</span>}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: 4 }}>
                      账号: {c.userEmail || c.userDisplayName || '-'} · 更新于 {new Date(c.updatedAt).toLocaleDateString('zh-CN')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                    <button
                      onClick={() => handleTogglePublic(c)}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                        cursor: 'pointer', fontSize: '0.8rem', padding: '4px 10px',
                        color: c.isPublic ? '#e65100' : '#2e7d32',
                      }}
                    >
                      {c.isPublic ? '设为私密' : '设为公开'}
                    </button>
                    <button
                      onClick={() => handleDeleteCandidate(c.id)}
                      style={{
                        background: 'none', border: 'none', color: '#ef5350',
                        cursor: 'pointer', fontSize: '0.8rem', padding: '4px 8px',
                      }}
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setEditingJob(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 16px', zIndex: 1000, overflowY: 'auto',
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 12, padding: 20,
            width: '100%', maxWidth: 720, boxSizing: 'border-box',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: '1.1rem', margin: 0 }}>编辑岗位</h3>
              <button
                onClick={() => setEditingJob(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--text-light)' }}
                aria-label="关闭"
              >
                ×
              </button>
            </div>

            {[
              { key: 'title', label: '岗位名称', type: 'text' },
              { key: 'company', label: '公司', type: 'text' },
              { key: 'location', label: '工作地点', type: 'text' },
              { key: 'salary', label: '薪资', type: 'text' },
              { key: 'contactInfo', label: '联系方式', type: 'text' },
              { key: 'publishDate', label: '发布日期', type: 'date' },
            ].map(({ key, label, type }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{label}</label>
                <input
                  type={type}
                  value={editingJob[key] || ''}
                  onChange={e => handleEditField(key, e.target.value)}
                  className="form-input"
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            ))}

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>岗位类型</label>
              <select
                value={editingJob.jobType}
                onChange={e => handleEditField('jobType', e.target.value)}
                className="form-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
              >
                {JOB_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>岗位分类</label>
              <select
                value={editingJob.category}
                onChange={e => handleEditField('category', e.target.value)}
                className="form-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
              >
                {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>岗位描述</label>
              <textarea
                value={editingJob.description || ''}
                onChange={e => handleEditField('description', e.target.value)}
                className="form-input"
                style={{ width: '100%', minHeight: 120, boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>任职要求</label>
              <textarea
                value={editingJob.requirements || ''}
                onChange={e => handleEditField('requirements', e.target.value)}
                className="form-input"
                style={{ width: '100%', minHeight: 80, boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={editSaving}
                style={{ flex: 1, padding: '10px 0' }}
              >
                {editSaving ? '保存中...' : '💾 保存'}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setEditingJob(null)}
                style={{ padding: '10px 20px' }}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Tab */}
      {tab === 'manage' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
              共 {jobs.length} 个岗位
            </span>
            <button
              className="btn"
              onClick={handleClearAll}
              style={{ background: '#ef5350', color: '#fff', fontSize: '0.85rem' }}
            >
              清除全部
            </button>
          </div>

          {jobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
              暂无岗位数据
            </div>
          ) : (
            jobs.map(job => (
              <div key={job.id} style={{
                padding: '12px 0', borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {job.title}
                      {job.isHidden && (
                        <span style={{ fontSize: '0.72rem', padding: '1px 6px', background: '#fff3e0', color: '#e65100', borderRadius: 4 }}>
                          已隐藏
                        </span>
                      )}
                      {job.isExpired && (
                        <span style={{ fontSize: '0.72rem', padding: '1px 6px', background: '#ffebee', color: '#c62828', borderRadius: 4 }}>
                          已过期
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
                      {job.company} · {job.location}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                    <button
                      onClick={() => openEditJob(job)}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                        cursor: 'pointer', fontSize: '0.78rem', padding: '3px 8px',
                        color: 'var(--primary)',
                      }}
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleToggleHide(job)}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                        cursor: 'pointer', fontSize: '0.78rem', padding: '3px 8px',
                        color: job.isHidden ? '#2e7d32' : '#e65100',
                      }}
                    >
                      {job.isHidden ? '显示' : '隐藏'}
                    </button>
                    <button
                      onClick={() => handleToggleExpire(job)}
                      style={{
                        background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                        cursor: 'pointer', fontSize: '0.78rem', padding: '3px 8px',
                        color: job.isExpired ? '#2e7d32' : '#c62828',
                      }}
                    >
                      {job.isExpired ? '恢复' : '过期'}
                    </button>
                    <button
                      onClick={() => handleDeleteJob(job.id)}
                      style={{
                        background: 'none', border: 'none', color: '#ef5350',
                        cursor: 'pointer', fontSize: '0.85rem', padding: '4px 8px',
                      }}
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* Users (用户管理) Tab */}
      {tab === 'users' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              className="form-input"
              placeholder="搜索用户名、邮箱、昵称..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchUsers(userSearch, showDeletedUsers)}
              style={{ flex: 1, minWidth: 200, maxWidth: 400 }}
            />
            <button className="btn btn-primary btn-sm" onClick={() => fetchUsers(userSearch, showDeletedUsers)}>搜索</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setUserSearch(''); fetchUsers('', showDeletedUsers); }}>重置</button>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-light)', marginLeft: 'auto' }}>
              <input
                type="checkbox"
                checked={showDeletedUsers}
                onChange={e => { setShowDeletedUsers(e.target.checked); fetchUsers(userSearch, e.target.checked); }}
              />
              显示已注销用户
            </label>
          </div>

          {usersLoading ? (
            <div style={{ color: 'var(--text-light)', padding: 20 }}>加载中...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--border-light)', textAlign: 'left' }}>
                    <th style={{ padding: '10px 12px' }}>用户名</th>
                    <th style={{ padding: '10px 12px' }}>显示名</th>
                    <th style={{ padding: '10px 12px' }}>邮箱</th>
                    <th style={{ padding: '10px 12px' }}>角色</th>
                    <th style={{ padding: '10px 12px' }}>状态</th>
                    <th style={{ padding: '10px 12px' }}>岗位数</th>
                    <th style={{ padding: '10px 12px' }}>消息数</th>
                    <th style={{ padding: '10px 12px' }}>注册时间</th>
                    <th style={{ padding: '10px 12px' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map(u => {
                    const isDeleted = !!u.deletedAt;
                    return (
                    <tr key={u.id} style={{
                      borderBottom: '1px solid var(--border-light)',
                      background: isDeleted ? '#f3f4f6' : (u.isDisabled ? '#fff5f5' : 'transparent'),
                      opacity: isDeleted ? 0.55 : (u.isDisabled ? 0.7 : 1),
                      textDecoration: isDeleted ? 'line-through' : 'none',
                    }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{u.username}</td>
                      <td style={{ padding: '10px 12px' }}>{u.nickname || u.displayName || '-'}</td>
                      <td style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-light)' }}>{u.email}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
                          background: u.role === 'admin' ? '#fef3c7' : '#e0f2fe',
                          color: u.role === 'admin' ? '#92400e' : '#0369a1',
                        }}>
                          {u.role === 'admin' ? '管理员' : '用户'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {isDeleted ? (
                          <span style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.8rem' }}>
                            已注销 · {new Date(u.deletedAt).toLocaleDateString('zh-CN')}
                          </span>
                        ) : u.isDisabled ? (
                          <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.8rem' }}>已禁用</span>
                        ) : (
                          <span style={{ color: '#16a34a', fontSize: '0.8rem' }}>正常</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{u.jobCount || 0}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{u.messagesSent || 0}</td>
                      <td style={{ padding: '10px 12px', fontSize: '0.78rem', color: 'var(--text-light)' }}>
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString('zh-CN') : '-'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {isDeleted ? (
                          <span style={{ color: 'var(--text-light)', fontSize: '0.75rem' }}>—</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                              onClick={() => handleToggleRole(u.id, u.role)}
                              title={u.role === 'admin' ? '降为普通用户' : '升为管理员'}
                            >
                              {u.role === 'admin' ? '降级' : '升级'}
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '3px 8px', fontSize: '0.75rem', color: u.isDisabled ? '#16a34a' : '#f59e0b' }}
                              onClick={() => handleToggleDisable(u.id, u.isDisabled)}
                            >
                              {u.isDisabled ? '启用' : '禁用'}
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '3px 8px', fontSize: '0.75rem', color: '#dc2626' }}
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              title="注销账号（PII 抹除，岗位作者显示为已注销）"
                            >
                              注销
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--text-light)' }}>
                共 {allUsers.length} 个用户
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminImport;
